import React, { useState } from 'react'
import { Banner, ChipSelect, Field, FoBtn, KV, Pill, ScreenShell, Section, TextArea } from './fieldUi'
import { useField } from './fieldContext'
import { MISS_REASONS, FAIL_REASONS } from '../demoData'
import { addDays, fmtDate, lb, money, money2, weekday } from '../FieldOpsKit'
import { clockOfStamp } from '../geo'
import { minutesLabel } from './shared'

/**
 * End of the day: every stop that was not finished needs a reason (and, if wanted, a new date) so nothing disappears;
 * returns picked up and cash collected are handed over; the day report goes to the office. Locked once sent.
 */
export default function EndDayScreen() {
    const f = useField()
    const { state, nav, actor, stops, summary, day, date } = f
    const isDriver = actor.kind === 'driver'
    const open = stops.filter((s) => s.state === 'pending' || s.state === 'current')
    const checkedIn = stops.filter((s) => s.state === 'current')[0] || null
    const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0)
    const myReturns = state.field.returns.filter((r) => r.actor === actor.key && r.at >= todayStart.getTime() && r.disposition === 'pickup' && r.status !== 'rejected')
    const cod = isDriver ? stops.filter((s) => s.state === 'done' && s.ref.payment).reduce((a, s) => a + s.ref.payment.amount, 0) : 0
    const [why, setWhy] = useState({})
    const [moveTo, setMoveTo] = useState({})
    const [handed, setHanded] = useState({})
    const [cashOk, setCashOk] = useState(false)
    const [odo, setOdo] = useState('')
    const [note, setNote] = useState('')
    const [tried, setTried] = useState(false)

    const options = []
    let d = date
    for (let i = 0; i < 5; i++) { d = addDays(d, 1); if (weekday(d) !== 0 && weekday(d) !== 6) options.push(d) }
    const missing = open.filter((s) => !why[s.id])
    const returnsPending = myReturns.filter((r) => !handed[r.id])
    const blocked = !!checkedIn
    const mins = day && day.startedAt ? Math.round((Date.now() - day.startedAt) / 60000) : 0

    const submit = () => {
        setTried(true)
        if (blocked || missing.length > 0 || returnsPending.length > 0 || (isDriver && cod > 0 && !cashOk)) { f.toast('Finish the highlighted items first.', 'warn'); return }
        const reasons = {}
        open.forEach((s) => { reasons[s.id] = { reason: why[s.id], moveTo: moveTo[s.id] || '', note: '' } })
        const startOdo = day ? day.startOdo : undefined
        const endOdo = odo === '' ? undefined : Number(odo)
        f.send({
            type: 'DAY_END', actorKey: actor.key, date: date, by: actor.name, odo: endOdo, miles: startOdo !== undefined && endOdo !== undefined ? endOdo - startOdo : summary.miles, note: note, reasons: reasons,
            handover: myReturns.length ? { returns: myReturns.map((r) => r.no) } : null, cash: cod,
            summary: summary.done + '/' + summary.total + (isDriver ? ' deliveries' : ' visits') + (open.length ? ', ' + open.length + ' not completed' : ''),
        })
        f.toast('Day report sent. Thank you!')
        nav.pop()
    }

    return (
        <ScreenShell title="End my day" subtitle={fmtDate(date)} onBack={nav.pop}
            footer={<React.Fragment><FoBtn kind="ghost" onClick={nav.pop}>Not yet</FoBtn><FoBtn kind="success" big onClick={submit}>Send day report</FoBtn></React.Fragment>}>
            {checkedIn ? <Banner tone="danger" title="You are still checked in.">Finish or cancel your visit at {checkedIn.name} before closing the day.</Banner> : null}
            <Section title="Your day">
                <div className="fo-grid2">
                    <div className="fo-stat"><span>{isDriver ? 'Delivered' : 'Visits done'}</span><strong>{summary.done}/{summary.total - summary.missed}</strong></div>
                    <div className="fo-stat"><span>{isDriver ? 'Weight delivered' : 'Orders'}</span><strong>{isDriver ? lb(stops.filter((s) => s.state === 'done').reduce((a, s) => a + s.weightLb, 0)) : money(summary.money)}</strong></div>
                    <div className="fo-stat"><span>Time worked</span><strong>{day && day.startedAt ? minutesLabel(mins) : '—'}</strong><small>{day && day.startedAt ? 'since ' + clockOfStamp(day.startedAt) : 'day not started'}</small></div>
                    <div className="fo-stat"><span>Not completed</span><strong style={{ color: open.length ? '#DC2626' : '#059669' }}>{open.length}</strong></div>
                </div>
            </Section>

            {open.length > 0 ? (
                <Section title={'Why were these not ' + (isDriver ? 'delivered' : 'visited') + '?'}>
                    <div className="fo-muted" style={{ paddingTop: 0 }}>The office sees the reason. You can pick a new day for each one.</div>
                    {open.map((s) => (
                        <div key={s.id} className="fo-unfin" style={tried && !why[s.id] ? { borderColor: '#FCA5A5' } : null}>
                            <strong>#{s.num} {s.name}</strong><span>{s.address}, {s.city}</span>
                            <Field label="Reason" error={tried && !why[s.id] ? 'Pick a reason' : ''}><ChipSelect options={isDriver ? FAIL_REASONS.concat(['Ran out of time', 'Vehicle problem']) : MISS_REASONS} value={why[s.id] || ''} onChange={(v) => setWhy({ ...why, [s.id]: v })} /></Field>
                            <Field label="New day (optional)"><div className="fo-chips"><button type="button" className={'fo-chip' + (!moveTo[s.id] ? ' is-on' : '')} onClick={() => setMoveTo({ ...moveTo, [s.id]: '' })}>Office decides</button>{options.map((o) => <button key={o} type="button" className={'fo-chip' + (moveTo[s.id] === o ? ' is-on' : '')} onClick={() => setMoveTo({ ...moveTo, [s.id]: o })}>{fmtDate(o)}</button>)}</div></Field>
                        </div>
                    ))}
                </Section>
            ) : <Banner tone="ok" title="Everything on your list is done.">Nice work.</Banner>}

            {myReturns.length > 0 ? (
                <Section title="Returns you took back today">
                    <div className="fo-muted" style={{ paddingTop: 0 }}>Confirm each one was handed to the warehouse.</div>
                    {myReturns.map((r) => (
                        <button key={r.id} type="button" className={'fo-checkrow' + (handed[r.id] ? ' is-on' : '') + (tried && !handed[r.id] ? ' is-bad' : '')} onClick={() => setHanded({ ...handed, [r.id]: !handed[r.id] })}>
                            <span className="fo-checkrow__box">{handed[r.id] ? '☑' : '☐'}</span><div><strong>{r.no} · {money2(r.total)}</strong><span>{r.lines.map((l) => l.qty + '× ' + l.name).join(', ')}</span></div><Pill tone={handed[r.id] ? 'green' : 'amber'}>{handed[r.id] ? 'Handed over' : 'Pending'}</Pill>
                        </button>
                    ))}
                </Section>
            ) : null}

            {isDriver && cod > 0 ? (
                <Section title="Cash and checks collected">
                    <KV label="Total collected today" strong>{money2(cod)}</KV>
                    <button type="button" className={'fo-checkrow' + (cashOk ? ' is-on' : '') + (tried && !cashOk ? ' is-bad' : '')} onClick={() => setCashOk(!cashOk)}>
                        <span className="fo-checkrow__box">{cashOk ? '☑' : '☐'}</span><div><strong>I handed this to the office</strong></div>
                    </button>
                </Section>
            ) : null}

            <Section title="Closing details">
                <Field label="Odometer at the end (optional)"><input className="fo-input" type="number" inputMode="numeric" value={odo} onChange={(e) => setOdo(e.target.value)} placeholder={day && day.startOdo ? 'Started at ' + day.startOdo.toLocaleString('en-US') : 'e.g. 84,310'} /></Field>
                <Field label="Anything the office should know?"><TextArea value={note} onChange={setNote} rows={3} placeholder="Optional" /></Field>
            </Section>
        </ScreenShell>
    )
}
